"use client";

import { ABOUT_SECTIONS } from "@/data/about-content";
import type { Locale } from "@/types/home";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";

/**
 * Sticky in-page nav (Tahawer / The Group / Leadership / Partners).
 * Sits directly beneath the main site header — whose height changes on scroll —
 * so we measure the live `<header>` and pin our `top` to it (avoids overlap).
 */
export default function AboutSubNav() {
  const locale = useLocale() as Locale;
  const [active, setActive] = useState(ABOUT_SECTIONS[0].id);
  const [top, setTop] = useState(96);
  const barRef = useRef<HTMLDivElement>(null);

  // Track the real header height (it shrinks h-20 → h-16 on scroll).
  useEffect(() => {
    const header = document.querySelector("header");
    let raf = 0;
    const measure = () => {
      raf = 0;
      if (header) setTop(header.getBoundingClientRect().height);
    };
    const onScroll = () => raf || (raf = requestAnimationFrame(measure));
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Scroll-spy.
  useEffect(() => {
    const els = ABOUT_SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean) as HTMLElement[];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id));
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const go = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - top - 8;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <div
      ref={barRef}
      style={{ top }}
      className="sticky z-40 border-b border-black/10 bg-white/85 backdrop-blur-md dark:border-white/10 dark:bg-ash/85"
    >
      <nav className="mx-auto flex max-w-360 gap-8 overflow-x-auto px-6 lg:px-10">
        {ABOUT_SECTIONS.map((s) => {
          const on = active === s.id;
          return (
            <button
              key={s.id}
              onClick={() => go(s.id)}
              className={`relative shrink-0 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                on ? "text-accent" : "text-ash/45 hover:text-ash dark:text-white/45 dark:hover:text-white"
              }`}
            >
              {s.label[locale]}
              {on && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-accent" />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
